import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, ShieldCheck, Camera } from "lucide-react";
import { SeatIcon } from "@/components/shop/SeatIcon";
import { reloadIfSame } from "@/lib/reloadSame";
import { LANGS, useLang } from "@/lib/i18n";
import { useCart } from "@/lib/cart";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const { count, setOpen } = useCart();
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    nav(`/catalog?q=${encodeURIComponent(q)}`);
  };

  const link = ({ isActive }) =>
    `text-sm uppercase tracking-widest font-display font-semibold transition-colors ${isActive ? "text-orange-500" : "text-slate-300 hover:text-white"}`;

  return (
    <header className="sticky top-0 z-40 border-b border-[#3B4157] bg-[#1F2230]/80 backdrop-blur-xl">
      <div className="hazard h-1 w-full opacity-80" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center gap-4 sm:gap-8">
        <Link to="/" onClick={reloadIfSame("/")} data-testid="header-logo-link" className="flex items-center gap-2 shrink-0">
          <span className="grid place-items-center w-9 h-9 bg-orange-500 text-[#1F2230]">
            <SeatIcon className="w-5 h-5" />
          </span>
          <span className="font-display font-black text-lg sm:text-xl uppercase tracking-tight leading-none">
            Sėdynės<span className="text-orange-500">.lt</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <NavLink to="/" end onClick={reloadIfSame("/")} className={link} data-testid="nav-home-link">{t("nav_home")}</NavLink>
          <NavLink to="/catalog" onClick={reloadIfSame("/catalog")} className={link} data-testid="nav-catalog-link">{t("nav_catalog")}</NavLink>
          <NavLink to="/seat-finder" onClick={reloadIfSame("/seat-finder")} className={link} data-testid="nav-finder-link">{t("nav_finder")}</NavLink>
        </nav>
        <form onSubmit={submit} className="hidden sm:flex flex-1 max-w-md items-center gap-2 border border-[#3B4157] bg-[#292D3C] px-3 h-10 focus-within:border-orange-500 transition-colors">
          <Search className="w-4 h-4 text-slate-500" />
          <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
            className="bg-transparent outline-none text-sm w-full placeholder:text-slate-500" />
        </form>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <Link to="/catalog" data-testid="header-mobile-catalog-link" className="sm:hidden grid place-items-center w-9 h-9 border border-[#3B4157] hover:border-orange-500" title={t("nav_catalog")}>
            <Search className="w-4 h-4" />
          </Link>
          <Link to="/seat-finder" data-testid="header-mobile-finder-link" className="md:hidden grid place-items-center w-9 h-9 border border-[#3B4157] hover:border-orange-500" title={t("nav_finder")}>
            <Camera className="w-4 h-4" />
          </Link>
          <select data-testid="lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
            className="lg:hidden h-9 px-1.5 bg-[#292D3C] border border-[#3B4157] text-xs font-mono-x font-bold uppercase text-orange-400 outline-none">
            {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
          <div className="hidden lg:flex border border-[#3B4157]" data-testid="lang-toggle">
            {LANGS.map((l) => (
              <button key={l} data-testid={`lang-${l}-btn`} onClick={() => setLang(l)}
                className={`px-2 sm:px-2.5 h-9 text-xs font-mono-x font-bold uppercase transition-colors ${lang === l ? "bg-orange-500 text-[#1F2230]" : "text-slate-400 hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>
          <button data-testid="header-cart-btn" onClick={() => setOpen(true)}
            className="relative grid place-items-center w-9 sm:w-10 h-9 border border-[#3B4157] hover:border-orange-500 transition-colors">
            <ShoppingCart className="w-4 h-4" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 min-w-5 h-5 px-1 grid place-items-center bg-orange-500 text-[#1F2230] text-[10px] font-mono-x font-bold">
                {count}
              </span>
            )}
          </button>
          <Link to="/admin" data-testid="header-admin-link" title={t("nav_admin")}
            className="hidden sm:grid place-items-center w-10 h-9 border border-[#3B4157] text-slate-400 hover:text-orange-500 hover:border-orange-500 transition-colors">
            <ShieldCheck className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}
